/**
 * The model's datasets, changed without leaving the model.
 *
 * It was two navigations. "Change datasets source" left the model for `/import_data`, a
 * page whose whole content is three large cards; choosing one navigated again, to a
 * `w-75` centred page headed "Edit model datasets" with a "Go back to Overview" link in
 * the corner. Changing a date meant leaving the thing the date is about.
 *
 * This repo has made the same move once before, and the reasoning carries over verbatim:
 * the Deployment TAB became a dialog because it was "a destination passed through once".
 *
 * The form inside is `MkAudiencePage`, mounted with `chrome="modal"`, NOT a second copy
 * of it. Its 660 lines are mostly save and validation wiring (which audiences exist,
 * which conversion mappings, what a live model may not do, how many datasets the tenant
 * may load at once), and a dialog that reimplemented any of that would drift from the
 * route that still serves deep links and every other build type.
 */
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import AudiencePage from '../../pages/model/buildDataset/AudiencePage';
import DatabaseImportPage from '../../pages/model/buildDataset/DatabaseImportPage';
import MkAudiencePage from '../../pages/model/buildDataset/build_dataset_pages/MkAudiencePage';
import MqaPage from '../../pages/model/buildDataset/build_dataset_pages/MqaPage';
import PqlPage from '../../pages/model/buildDataset/build_dataset_pages/PqlPage';
import checkPermissions from '../CheckPermissions';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import './datasetModal.css';

type Source = 'integrations' | 'csv' | 'redshift';

/** What the dialog is called, following what is on screen rather than naming itself once. */
const TITLES: Record<Source | 'none', string> = {
  none: 'Create datasets',
  integrations: 'Datasets',
  csv: 'Upload datasets',
  redshift: 'Import datasets',
};

/**
 * `superKudu` marks a source only HG staff may pick.
 *
 * The page this replaces wrapped its Redshift card in
 * `checkPermissions(PROFILES_PERMISSIONS.SUPERKUDU, ...)`, and the first version of this
 * dialog rendered all three unconditionally, which showed every customer an internal
 * source they cannot use. Carried here rather than left to the caller, so a source added
 * to this list has to say who may see it.
 */
const SOURCES: {
  id: Source;
  label: string;
  hint: string;
  icon: string;
  superKudu?: boolean;
}[] = [
  {
    id: 'integrations',
    label: 'Your integrations',
    hint: 'CRM, marketing automation, S3',
    icon: 'fas fa-network-wired',
  },
  {
    id: 'csv',
    label: 'CSV upload',
    hint: 'One file per dataset',
    icon: 'fas fa-file-csv',
  },
  {
    id: 'redshift',
    label: 'HG Insights database',
    hint: 'Redshift, internal only',
    icon: 'fas fa-database',
    superKudu: true,
  },
];

export function DatasetModal({
  show,
  onHide,
  modelType = 'customer_fit',
  /**
   * Whether this model already has datasets.
   *
   * It decides where the dialog opens. A model with datasets opens on its parameters,
   * because changing a date is the common act and the source is already settled. A
   * model with none opens on the source, because that is the first decision and the
   * page this replaces made it on a screen of its own.
   *
   * Opening on the parameters must not strand the other sources, so a model with
   * datasets gets a "Switch source" button that goes back to the picker.
   */
  hasDatasets,
}: {
  show: boolean;
  onHide: () => void;
  hasDatasets: boolean;
  /**
   * An engagement model has one source, its activity, so the dialog skips the
   * picker and hosts that model's own form: the lead one for pql, pql2 and
   * mql, the account one for mqa. Fit keeps the three sources above.
   */
  modelType?: string;
}) {
  const engagement = modelType !== 'customer_fit';
  const [source, setSource] = useState<Source | null>(null);

  // Reset on every open rather than on close, so a dialog reopened after a cancel asks
  // again instead of resuming a choice the reader backed out of.
  useEffect(() => {
    if (show) setSource(hasDatasets ? 'integrations' : null);
  }, [show, hasDatasets]);

  // All three are hosted here. The routes stay mounted for deep links and for anyone
  // who has one bookmarked.
  const choose = (picked: Source) => setSource(picked);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
      scrollable
      dialogClassName="dsmodal"
      aria-labelledby="dataset-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="dataset-modal-title" className="dsmodal-title">
          {engagement ? 'Datasets' : TITLES[source ?? 'none']}
        </Modal.Title>
        {!engagement && hasDatasets && source && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="ms-3"
            onClick={() => setSource(null)}
          >
            Switch source
          </Button>
        )}
      </Modal.Header>
      <Modal.Body>
        {show && engagement && modelType === 'mqa' && (
          <MqaPage chrome="modal" onDone={onHide} onCancel={onHide} />
        )}
        {show && engagement && modelType !== 'mqa' && (
          <PqlPage chrome="modal" onDone={onHide} onCancel={onHide} />
        )}
        {show && !engagement && !source && (
          <div className="dsmodal-sources">
            <p className="dsmodal-lead">Where should the data come from?</p>
            <div className="dsmodal-source-list">
              {SOURCES.map((option) => {
                const tile = (
                  <button
                    key={option.id}
                    type="button"
                    className="dsmodal-source"
                    onClick={() => choose(option.id)}
                  >
                    <span className="dsmodal-source-mark" aria-hidden>
                      <i className={option.icon} />
                    </span>
                    <span className="dsmodal-source-text">
                      <span className="dsmodal-source-label">
                        {option.label}
                      </span>
                      <span className="dsmodal-source-hint">{option.hint}</span>
                    </span>
                  </button>
                );
                return option.superKudu ? (
                  <React.Fragment key={option.id}>
                    {checkPermissions(PROFILES_PERMISSIONS.SUPERKUDU, tile)}
                  </React.Fragment>
                ) : (
                  tile
                );
              })}
            </div>
          </div>
        )}
        {/* Mounted only while open, so the form reads the store fresh each time rather
            than holding whatever it had when the page first rendered. Its own effects
            load the tenant's audiences and conversion mappings on mount. */}
        {show && !engagement && source === 'integrations' && (
          <MkAudiencePage chrome="modal" onDone={onHide} onCancel={onHide} />
        )}
        {show && !engagement && source === 'csv' && (
          <AudiencePage chrome="modal" onDone={onHide} onCancel={onHide} />
        )}
        {show && !engagement && source === 'redshift' && (
          <DatabaseImportPage
            chrome="modal"
            onDone={onHide}
            onCancel={onHide}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}

export default DatasetModal;
